import React, { useState, useEffect } from "react";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import { Pie } from "react-chartjs-2";
import { Row, Col, Nav } from "react-bootstrap";
import { ProgramPieChart } from "./ProgramPieChart";
import { loadingCard } from "../../../helpers/loading";

ChartJS.register(ArcElement, Tooltip, Legend);

interface RawDataItem {
  level: string;
  level_id?: string | number;
  level_order: number;
  program: string;
  program_id?: string | number;
}

interface LevelPieChartProps {
  data: any;
  selectedProgramTab?: string | number | null;
  setSelectedProgramTab?: React.Dispatch<React.SetStateAction<string | number | null>>;
  showHeader?: boolean;
}

export const LevelPieChart = ({ data, selectedProgramTab = 0, setSelectedProgramTab, showHeader = false }: LevelPieChartProps) => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [selectedProgramId, setSelectedProgramId] = useState<string | number | null>(0);
  const [fetchedData, setFetchedData] = useState<any[]>([]);
  const [programGroups, setProgramGroups] = useState<any[]>([]);
  const [levelGroups, setLevelGroups] = useState<any[]>([]);

  useEffect(() => {
    if (data) {
      const { programData } = data;
      setFetchedData(programData);
      const pGroups = groupByProgram(programData);
      setProgramGroups(pGroups);
      const lGroups = groupByLevel(programData, selectedProgramId ?? undefined);
      setLevelGroups(lGroups);
    }
    setIsLoading(false);
  }, [data]);

  const onSelectProgram = (programId: number) => {
    setSelectedProgramId(programId);
    if (programId !== 0) {
      // setSelectedProgramTab && setSelectedProgramTab(programId);
      const lGroups = groupByLevel(fetchedData, programId ?? undefined);
      setLevelGroups(lGroups);
    }
  };

  function groupByProgram(data: RawDataItem[]) {
    const counts: Record<string, { name: string; count: number }> = {};
    data?.forEach((item: any) => {
      const key = item.program_id ?? item.program;
      const name = item.program;
      if (!counts[key]) {
        counts[key] = { name, count: 0 };
      }
      counts[key].count += 1;
    });
    return Object.entries(counts).map(([id, { name, count }]) => ({ id, label: name, numberCases: count }));
  }

  function groupByLevel(data: RawDataItem[], programId?: string | number) {
    const filtered = programId ? data.filter((item: any) => item.program_id == programId) : data;
    const counts: Record<string, { name: string; count: number; order: number }> = {};
    filtered?.forEach((item: any) => {
      const key = item.level_id ?? item.level ?? "NO LEVEL";
      const name = item.level && item.level.trim() !== "" ? item.level.toUpperCase() : "NO LEVEL";
      const order = item.level && item.level.trim() !== "" ? item.level_order : 999;
      if (!counts[key]) {
        counts[key] = { name, count: 0, order };
      }
      counts[key].count += 1;
    });
    return Object.entries(counts)
      .map(([id, { name, count, order }]) => ({ id, label: name, numberCases: count, level_order: order }))
      .sort((a: any, b: any) => a.level_order - b.level_order);
  }

  const chartData = {
    labels: levelGroups.length > 0 ? levelGroups.map((item) => item.label) : ["No data available"],
    datasets: [
      {
        label: "# People",
        data: levelGroups.length > 0 ? levelGroups.map((item) => item.numberCases) : [0],
        backgroundColor: ["#ed2f2f", "#DA7E39", "#B2BEB5", "#5A7082", "#5D82A0", "#F4A259", "#B5B2A4", "#A0C1B8", "#E07A5F", "#3D5A80"],
        borderWidth: 1,
      },
    ],
  };

  const options = {
    plugins: {
      maintainAspectRatio: false,
      title: {
        display: true,
        text: "Level Assignment",
        color: "#5a7082",
        font: {
          size: 18,
          weight: 500,
        },
      },
      legend: {
        display: true,
        position: "top" as const,
        align: "center" as const,
        labels: {
          boxWidth: 25,
          font: {
            size: 14,
          },
          padding: 15,
        },
      },
    },
    layout: {
      padding: {
        top: 0,
        bottom: 0,
      },
    },
  };

  if (isLoading) {
    return loadingCard();
  }

  return (
    <>
      {showHeader && <h5 className="app-header-text">Programs and Levels</h5>}
      <Row className="justify-content-start mb-3">
        <Col xs={5} lg={4}>
          <Nav variant="pills" className="flex-column">
            <Nav.Item className="d-grid" key={0}>
              <Nav.Link
                eventKey={0}
                className={selectedProgramId === 0 ? "nav-vertical-active" : "nav-vertical"}
                onClick={() => onSelectProgram(0)}
                style={{ textAlign: "left", fontWeight: 500, cursor: "pointer" }}
              >
                {"All Programs"}
              </Nav.Link>
            </Nav.Item>
            {programGroups.map((program) => (
              <Nav.Item className="d-grid" key={program.id}>
                <Nav.Link
                  eventKey={program.id}
                  className={selectedProgramId === program.id ? "nav-vertical-active" : "nav-vertical"}
                  onClick={() => {
                    onSelectProgram(program.id);
                  }}
                  style={{ textAlign: "left", fontWeight: 500, cursor: "pointer" }}
                >
                  {program.label}
                </Nav.Link>
              </Nav.Item>
            ))}
          </Nav>
        </Col>
        <Col xs={7} lg={8}>
          {selectedProgramId === 0 ? (
            <ProgramPieChart data={data} />
          ) : (
            <div style={{ height: 350, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Pie data={chartData} options={options} width={350} height={350} style={{ maxHeight: "100%", maxWidth: "100%" }} />
            </div>
          )}
        </Col>
      </Row>
    </>
  );
};
